import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Plus, Search, Heart, Link as LinkIcon, Phone, MapPin, Building, X, User, CheckCircle, Lock, Clock, Check, Eye, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { gradientText, gradientButton } from '../theme';
import { useSearch } from '../hooks/useSearch';
import { useNotifications } from '../hooks/useNotifications';
import { SearchResult as ApiSearchResult } from '../services/searchService';
import { theme } from '../theme';
import { supabase } from '../lib/supabase';
import { useAppSettings } from '../hooks/useAppSettings';
import { useAuthContext } from '../contexts/AuthContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/phoneInput.css';
import { useCachedData } from '../hooks/useCachedData';
import PaymentFlow from '../components/PaymentFlow';
import { toast } from 'react-hot-toast';

// Composant pour afficher le statut avec un dégradé SVG (identique à celui de Profile.tsx)
const StatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  return (
    <div className="inline-block">
      <svg width="100" height="28" viewBox="0 0 100 28" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="statusGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={theme.colors.primary.start} />
            <stop offset="100%" stopColor={theme.colors.primary.end} />
          </linearGradient>
        </defs>
        <rect width="98" height="26" x="1" y="1" rx="13" stroke="url(#statusGradient)" strokeWidth="2" fill="transparent" />
        <text x="50" y="18" fontFamily="Arial" fontSize="12" fontWeight="600" fill="white" textAnchor="middle">
          {status === 'couple' ? t('profile.relations.status.en_couple') : t(`profile.relations.status.${status}`)}
        </text>
      </svg>
    </div>
  );
};

interface SearchResult {
  searchedPerson: {
    photo: string;
    name: string;
    country: string;
    city: string;
    status: string;
    matchPercentage: number;
    confirmed: boolean;
    created_at: string;
    comment: string | null;
    searchCount?: number; // Nouveau champ
    creator: {
      name: string;
      photo: string;
      link?: string;
    };
    partner?: {
      name: string;
      photo: string;
      link?: string;
    };
    otherRelations?: Array<{
      status: string;
      created_at: string;
      matchPercentage: number;
      confirmed: boolean;
      comment?: string | null;
      creator: {
        name: string;
        photo: string;
        link?: string;
      };
    }>;
  };
}

type SearchMode = 'main' | 'single';

// Interface pour les photos du bucket "couples"
interface CarouselPhoto {
  name: string;
  url: string;
}

// Fonction pour récupérer les photos du carrousel
const fetchCarouselPhotos = async (): Promise<CarouselPhoto[]> => {
  try {
    const { data, error } = await supabase.storage
      .from('couples')
      .list('', {
        sortBy: { column: 'name', order: 'asc' }
      });
    
    if (error) {
      console.error('Erreur lors de la récupération des photos:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      return [];
    }
    
    // Filtrer uniquement les fichiers (pas les dossiers) et vérifier qu'ils sont valides
    const photoFiles = data.filter(item => 
      !item.id.endsWith('/') && 
      item.name && 
      item.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)
    );
    
    // Vérification supplémentaire - si aucun fichier valide n'est trouvé
    if (photoFiles.length === 0) {
      return [];
    }
    
    const photoUrls = await Promise.all(
      photoFiles.map(async (file) => {
        try {
          const { data: { publicUrl } } = supabase.storage
            .from('couples')
            .getPublicUrl(file.name);
          
          return {
            name: file.name,
            url: publicUrl
          };
        } catch (err) {
          console.error(`Erreur lors de la récupération de l'URL de ${file.name}:`, err);
          return null;
        }
      })
    );
    
    // Filtrer les URLs nulles (en cas d'erreur)
    const validPhotoUrls = photoUrls.filter(photo => photo !== null) as CarouselPhoto[];
    return validPhotoUrls;
  } catch (err) {
    console.error('Exception lors de la récupération des photos:', err);
    throw err;
  }
};

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { search: textSearch, results } = useSearch();
  const { createNotification } = useNotifications();
  const { getRelationUnlockPrice, formatPrice } = useAppSettings();
  const { user } = useAuthContext();
  
  // États
  const [fullName, setFullName] = useState(() => localStorage.getItem('home_fullName') || '');
  const [phones, setPhones] = useState<string[]>(() => {
    const savedPhones = localStorage.getItem('home_phones');
    return savedPhones ? JSON.parse(savedPhones) : [''];
  });
  const [country, setCountry] = useState(() => localStorage.getItem('home_country') || '');
  const [city, setCity] = useState(() => localStorage.getItem('home_city') || '');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [activeSearchMode, setActiveSearchMode] = useState<SearchMode>('main');
  const [hasSearched, setHasSearched] = useState(false);
  const [isResultLocked, setIsResultLocked] = useState(true);
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);
  const [showUnlockConfirmation, setShowUnlockConfirmation] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);
  const [waitingForDonation, setWaitingForDonation] = useState(false);
  const [waitingTime, setWaitingTime] = useState<number>(90);
  const [loadingDots, setLoadingDots] = useState('');
  const [showInfoPopup, setShowInfoPopup] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Effet pour mettre à jour l'URL quand showAllResults change
  useEffect(() => {
    if (showAllResults) {
      navigate({
        pathname: location.pathname,
        search: '?show_modal=all_results',
      }, { replace: true });
    } else {
      // Si le modal est fermé, retirer le paramètre de l'URL
      if (location.search.includes('show_modal=all_results')) {
        navigate({
          pathname: location.pathname,
          search: '',
        }, { replace: true });
      }
    }
  }, [showAllResults, navigate, location.pathname, location.search]);
  
  // Utilisation de useCachedData pour les photos du carrousel
  const { 
    data: carouselPhotosData, 
    isLoading: isLoadingPhotos, 
    error: photoErrorObj,
    invalidateCache: _invalidateCarouselCache
  } = useCachedData<CarouselPhoto[]>(
    'carousel-photos-couples',
    fetchCarouselPhotos,
    24 * 60 * 60 * 1000, // Cache de 24 heures
    []
  );
  
  // S'assurer que carouselPhotos n'est jamais null
  const carouselPhotos = carouselPhotosData || [];
  
  // Conversion de l'erreur en boolean pour maintenir la compatibilité
  const photoError = !!photoErrorObj;
  
  // Référence pour le conteneur des résultats
  const resultsRef = useRef<HTMLDivElement>(null);

  // Récupérer le prix de déverrouillage
  const unlockPrice = getRelationUnlockPrice();
  const formattedUnlockPrice = formatPrice(unlockPrice);

  // Charger les données sauvegardées ou utiliser les valeurs par défaut
  useEffect(() => {
    localStorage.setItem('home_fullName', fullName);
    localStorage.setItem('home_phones', JSON.stringify(phones));
    localStorage.setItem('home_country', country);
    localStorage.setItem('home_city', city);
  }, [fullName, phones, country, city]);

  // Séparer les photos en deux groupes pour les carrousels
  const { leftTrackPhotos, rightTrackPhotos } = useMemo(() => {
    if (carouselPhotos.length === 0) {
      return { leftTrackPhotos: [], rightTrackPhotos: [] };
    }
    
    const middle = Math.ceil(carouselPhotos.length / 2);
    return {
      leftTrackPhotos: carouselPhotos.slice(0, middle),
      rightTrackPhotos: carouselPhotos.slice(middle)
    };
  }, [carouselPhotos]);

  // Nettoyer le stockage lors de la fermeture de l'application
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem('home_fullName');
      localStorage.removeItem('home_phones');
      localStorage.removeItem('home_country');
      localStorage.removeItem('home_city');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Timer pour l'attente de don
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (waitingForDonation && waitingTime > 0) {
      timer = setInterval(() => {
        setWaitingTime(prev => {
          if (prev <= 1) {
            setWaitingForDonation(false);
            setShowThankYou(true);
            // Afficher le message de remerciement pendant 2 secondes
            setTimeout(() => {
              setShowThankYou(false);
              confirmUnlock();
            }, 2000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [waitingForDonation, waitingTime]);

  // Effet pour l'animation des points
  useEffect(() => {
    let dotsTimer: NodeJS.Timeout;
    if (waitingForDonation) {
      dotsTimer = setInterval(() => {
        setLoadingDots(dots => dots.length >= 3 ? '' : dots + '.');
      }, 500);
    }
    return () => {
      if (dotsTimer) clearInterval(dotsTimer);
    };
  }, [waitingForDonation]);

  // Rendu du carrousel de photos
  const renderPhotoCarousel = () => {
    if (isLoadingPhotos) {
      return (
        <div className="bg-[#0A0A0A] rounded-2xl p-8 text-center my-4">
          <div className="flex flex-col items-center justify-center gap-3">
            <Loader className="w-8 h-8 text-primary-end animate-spin" />
            <p className="text-gray-400">{t('singleSearch.photoCarousel.loading')}</p>
          </div>
        </div>
      );
    }
    
    if (photoError) {
      return (
        <div className="bg-[#0A0A0A] rounded-2xl p-8 text-center my-4">
          <p className="text-red-400">{t('singleSearch.photoCarousel.error')}</p>
        </div>
      );
    }
    
    if (carouselPhotos.length === 0) {
      return null; // Ne rien afficher si aucune photo n'est disponible
    }
    
    return (
      <div className="my-6">
        <div className="photo-carousel-container mx-auto overflow-hidden relative">
          {/* Piste gauche avec mouvement pendulaire */}
          <div className="flex photo-carousel-track-left">
            {leftTrackPhotos.map((photo, index) => (
              <div 
                key={`left-${photo.name}-${index}`}
                className={`photo-carousel-item flex-shrink-0 mx-2 pointer-events-none select-none ${index % 5 === 0 ? 'mt-4' : index % 5 === 1 ? 'mt-6' : index % 5 === 2 ? 'mt-2' : index % 5 === 3 ? 'mt-8' : 'mt-5'}`}
                style={{ 
                  animationDelay: `${index * 0.5}s`, 
                  width: '120px', 
                  height: '180px' 
                }}
              >
                <img 
                  src={photo.url} 
                  alt={`Carousel image ${index + 1}`}
                  className="object-cover w-full h-full rounded-lg shadow-lg user-select-none"
                  draggable="false"
                />
              </div>
            ))}
          </div>
          
          {/* Piste droite avec mouvement pendulaire inverse */}
          <div className="flex photo-carousel-track-right mt-10">
            {rightTrackPhotos.map((photo, index) => (
              <div 
                key={`right-${photo.name}-${index}`}
                className={`photo-carousel-item flex-shrink-0 mx-2 pointer-events-none select-none ${index % 5 === 0 ? 'mt-3' : index % 5 === 1 ? 'mt-7' : index % 5 === 2 ? 'mt-1' : index % 5 === 3 ? 'mt-5' : 'mt-4'}`}
                style={{ 
                  animationDelay: `${index * 0.5}s`, 
                  width: '120px', 
                  height: '180px' 
                }}
              >
                <img 
                  src={photo.url} 
                  alt={`Carousel image ${index + 1}`}
                  className="object-cover w-full h-full rounded-lg shadow-lg user-select-none"
                  draggable="false"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleAddPhone = () => {
    setPhones([...phones, '']);
  };

  const handleRemovePhone = (index: number) => {
    setPhones(phones.filter((_, i) => i !== index));
  };

  const handlePhoneChange = (index: number, value: string) => {
    const newPhones = [...phones];
    newPhones[index] = value;
    setPhones(newPhones);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSearching) return;

    // Définir le mode de recherche actif
    setActiveSearchMode('main');

    // Vérifier si l'utilisateur est connecté
    if (!user) {
      navigate('/login');
      return;
    }

    setIsSearching(true);
    setSearchResult(null); // Réinitialiser les résultats précédents

    try {
      let results: ApiSearchResult[] = [];
      
      // Effectuer une recherche textuelle avec les paramètres fournis
      if (fullName || phones[0] || country || city) {
        const textResults = await textSearch({
          name: fullName || undefined,
          phone: phones[0] || undefined,
          country: country || undefined,
          city: city || undefined
        });
        
        const validTextResults = textResults.map((result: ApiSearchResult) => ({
          ...result,
          match_percentage: typeof result.match_percentage === 'number' 
            ? result.match_percentage 
            : Number(result.match_percentage) || 0,
          created_at: result.created_at || new Date().toISOString(),
          comment: result.comment || null
        }));
        
        if (validTextResults.length > 0) {
          results = [...validTextResults];
        }
      }

      // Trier les résultats par pourcentage de correspondance
      results.sort((a, b) => {
        const aPercentage = typeof a.match_percentage === 'number' ? a.match_percentage : Number(a.match_percentage) || 0;
        const bPercentage = typeof b.match_percentage === 'number' ? b.match_percentage : Number(b.match_percentage) || 0;
        return bPercentage - aPercentage;
      });

      if (results.length > 0) {
        // Regrouper les résultats par nom de personne
        const groupedResults = results.reduce((acc, result) => {
          if (!acc[result.name]) {
            acc[result.name] = [];
          }
          acc[result.name].push(result);
          return acc;
        }, {} as Record<string, ApiSearchResult[]>);

        // Prendre le premier résultat comme résultat principal
        const firstResult = results[0];
        const otherRelations = groupedResults[firstResult.name]
          .slice(1)
          .map(relation => ({
            status: relation.status as string,
            created_at: relation.created_at,
            matchPercentage: typeof relation.match_percentage === 'number' 
              ? relation.match_percentage 
              : Number(relation.match_percentage) || 0,
            confirmed: relation.confirmed,
            comment: relation.comment,
            creator: {
              name: relation.creator_name,
              photo: relation.creator_photo,
              link: relation.creator_link || undefined
            }
          }));
        
        // Vérifier si l'utilisateur est connecté avant d'envoyer des notifications
        const { data: userData } = await supabase.auth.getUser();
        const currentUser = userData?.user;
        
        // Envoyer une notification à la personne qui a créé/certifié la relation
        try {
          // Toujours envoyer une notification au créateur de la relation
          await createNotification({
            userId: firstResult.user_id,
            translationKey: 'relationSearchedByUser',
            translationParams: {
              user: currentUser ? (currentUser.user_metadata?.full_name || currentUser.email?.split('@')[0] || 'Un utilisateur') : 'Un visiteur anonyme',
              name: firstResult.name
            }
          });
          
          // Ne pas envoyer de notification à l'utilisateur qui fait une recherche sur son propre profil
          if (currentUser && currentUser.id !== firstResult.user_id) {
          await createNotification({
              userId: currentUser.id,
              translationKey: 'search_result',
            translationParams: {
                count: results.length.toString()
            }
          });
          }
        } catch (notificationError) {
          console.error("Error sending notifications:", notificationError);
          // Ne pas bloquer le flux en cas d'erreur de notification
        }
        
        setSearchResult({
          searchedPerson: {
            photo: firstResult.photo,
            name: firstResult.name,
            country: firstResult.country || '',
            city: firstResult.city || '',
            status: firstResult.status,
            matchPercentage: firstResult.match_percentage,
            confirmed: firstResult.confirmed,
            created_at: firstResult.created_at,
            comment: firstResult.comment,
            searchCount: firstResult.search_count || 0, // Ajouter le compteur
            creator: {
              name: firstResult.creator_name,
              photo: firstResult.creator_photo,
              link: firstResult.creator_link || undefined
            },
            partner: undefined,
            otherRelations: otherRelations
          }
        });
      } else {
        setSearchResult(null);
        }

      setHasSearched(true); // Marquer qu'une recherche a été effectuée
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setSearchResult(null);
    } finally {
      setIsSearching(false);
      
      // Défiler vers les résultats
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        }
      }, 300);
    }
  };

  const handleUnlock = () => {
    // L'utilisateur est forcément connecté maintenant, pas besoin de vérifier
    setShowPaymentModal(true);
  };

  const confirmUnlock = async () => {
    try {
      // Déverrouiller directement les résultats
      setIsResultLocked(false);
      setShowUnlockConfirmation(false);
      
      // Sauvegarder dans le localStorage
      localStorage.setItem('search_unlocked', 'true');
      
      // Afficher un message de succès
      toast.success(t('search.unlockSuccess'));
      
    } catch (error) {
      console.error('Erreur lors du déverrouillage:', error);
      toast.error(t('search.unlockError'));
    }
  };

  const cancelUnlock = () => {
    // Fermer le modal de confirmation
    setShowUnlockConfirmation(false);
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className={`text-3xl lg:text-4xl xl:text-5xl font-bold ${gradientText} mb-1`}>
          {t('home.title')}
        </h2>
        <p className="text-gray-400 text-xs lg:text-sm italic font-light tracking-wide">
          {t('home.subtitle')}
        </p>
      </div>

      {/* Afficher le carrousel uniquement quand aucune recherche n'est en cours */}
      {!hasSearched && carouselPhotos.length > 0 && renderPhotoCarousel()}

      <form onSubmit={handleSearch} className="space-y-4">
        <div>
          <p className={`text-lg mb-4 ${gradientText}`}>{t('home.additionalInfo.title')}</p>
          
          {/* Message défilant avec le design de Settings.tsx */}
          <div className="mb-4 overflow-hidden" style={{ display: 'none' }}>
            <div className="w-full overflow-hidden bg-gradient-to-r from-black to-[#0A0A0A] rounded-xl py-3 border-l border-primary-start/30 shadow-md relative crawl-container crawl-glow light-sweep">
              {/* Effet de bordure brillante - opacité réduite */}
              <div className="absolute inset-0 border border-primary-start/10 rounded-xl"></div>
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-start to-primary-end opacity-30"></div>
              <div className="absolute top-0 left-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-start to-primary-end opacity-40"></div>
              
              {/* Effet lumineux accentué sur la bordure gauche - subtil */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-start via-primary-end to-primary-start opacity-20 blur-sm"></div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-12 w-0.5 bg-primary-end opacity-40 blur-[1px] animate-pulse"></div>
              
              <div className="relative pl-3">
                <div className="crawl-text text-sm">
                  <span>
                    {t('home.crawlText.part2')}
                    <span className="highlight-word">{t('home.crawlText.part1')}</span>
                    {t('home.crawlText.part3')}
                    <span className="highlight-word">{t('home.crawlText.part4')}</span>
                    {t('home.crawlText.part6')}
                    <span className="highlight-word">{t('home.crawlText.part5')}</span>
                    {t('home.crawlText.part7')}
                    <span className="mx-28">•</span>
                    {t('home.crawlText.part2')}
                    <span className="highlight-word">{t('home.crawlText.part1')}</span>
                    {t('home.crawlText.part3')}
                    <span className="highlight-word">{t('home.crawlText.part4')}</span>
                    {t('home.crawlText.part6')}
                    <span className="highlight-word">{t('home.crawlText.part5')}</span>
                    {t('home.crawlText.part7')}
                    <span className="mx-28">•</span>
                    {t('home.crawlText.part2')}
                    <span className="highlight-word">{t('home.crawlText.part1')}</span>
                    {t('home.crawlText.part3')}
                    <span className="highlight-word">{t('home.crawlText.part4')}</span>
                    {t('home.crawlText.part6')}
                    <span className="highlight-word">{t('home.crawlText.part5')}</span>
                    {t('home.crawlText.part7')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-primary-end text-white"
                placeholder={t('home.additionalInfo.fullName')}
              />
            </div>

            <div className="space-y-2">
              {phones.map((phone, index) => (
                <div key={index} className="relative flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" style={{ zIndex: 1 }}>
                      <Phone />
                    </div>
                    <PhoneInput
                      country={'cm'}
                      value={phone}
                      onChange={(value) => handlePhoneChange(index, value)}
                      containerClass="w-full"
                      inputClass="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-24 pr-4 py-3 focus:outline-none focus:border-primary-end text-white"
                      dropdownClass="!bg-[#0A0A0A] !text-white !border-gray-800 custom-dropdown"
                      searchClass="!bg-[#0A0A0A] !text-[#B8A44E] !border-gray-800 focus:!border-[#B8A44E]"
                      buttonStyle={{
                        background: 'transparent',
                        border: 'none',
                        position: 'absolute',
                        left: '2.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2
                      }}
                      inputStyle={{
                        background: '#0A0A0A',
                        border: '1px solid rgb(31 41 55)',
                        borderRadius: '0.75rem',
                        color: 'white',
                        width: '100%',
                        height: '100%',
                        paddingLeft: '6rem'
                      }}
                      dropdownStyle={{
                        background: '#0A0A0A',
                        border: '1px solid rgb(31 41 55)',
                        color: 'white'
                      }}
                      specialLabel=""
                      enableSearch={true}
                      disableSearchIcon={true}
                      countryCodeEditable={false}
                    />
                  </div>
                  {phones.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePhone(index)}
                      className="px-3 py-3 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500/20 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddPhone}
                className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl px-4 py-3 flex items-center justify-center gap-2 text-gray-400 hover:border-primary-end transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('home.additionalInfo.addNumber')}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-primary-end text-white"
                  placeholder={t('home.additionalInfo.country')}
                />
              </div>
              <div className="relative">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-primary-end text-white"
                  placeholder={t('home.additionalInfo.city')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pop-up d'information */}
        {showInfoPopup && (
          <div className="my-4 animate-fadeIn relative">
            <div className="bg-gradient-to-r from-primary-start to-primary-end p-px rounded-xl shadow-lg">
              <div className="bg-[#0A0A0A] rounded-xl p-4 relative">
                <button 
                  onClick={() => setShowInfoPopup(false)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
                
                <p className="text-white text-sm mb-3">
                  {t('home.infoPopup.message')}
                </p>
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={!fullName && !phones[0] && !country && !city || isSearching}
          className={`w-full ${gradientButton} disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-lg ${activeSearchMode === 'single' ? 'opacity-70 hover:opacity-90' : ''}`}
        >
          {isSearching ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Search className="w-5 h-5" />
          )}
          {isSearching ? t('home.searching') : t('home.search')}
        </button>
      </form>

      {searchResult && (
        <div ref={resultsRef} className="mt-8 bg-[#0A0A0A] rounded-2xl overflow-hidden relative">
          <div className={`p-4 space-y-4 ${waitingForDonation || showThankYou ? 'blur-sm' : ''}`}>
            {/* Titre des résultats et bouton sur la même ligne */}
            <div className="flex items-center justify-between">
              <h3 className={`text-xl font-bold ${gradientText}`}>{t('home.results.title')}</h3>
              {results && results.length > 1 && (
                <button
                  onClick={() => setShowAllResults(true)}
                  className={`${gradientButton} text-white text-sm px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity`}
                >
                  {t('home.results.viewAll')} ({results.length})
                </button>
              )}
            </div>
            
            {/* Photo et informations principales */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* Photo principale du résultat */}
              <div className="w-full h-[320px] md:w-48 md:h-48 mx-auto rounded-2xl overflow-hidden flex-shrink-0">
                <img 
                  src={searchResult.searchedPerson.photo} 
                  alt={searchResult.searchedPerson.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Informations principales */}
              <div className="flex-1 min-w-0 space-y-3">
                {/* Nom avec compteur de vues */}
                <div className="flex flex-col">
                  <h3 className="text-2xl font-semibold text-white mb-1">
                  {searchResult.searchedPerson.name}
                </h3>
                  {(searchResult.searchedPerson.searchCount ?? 0) > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Eye className="w-3 h-3" /> 
                      <span>MyView {searchResult.searchedPerson.searchCount}</span>
                    </div>
                  )}
                </div>

                {/* Éléments à cacher lorsque verrouillé */}
                {!isResultLocked && (
                  <>
                {/* Localisation */}
                    <div className="flex items-center gap-2 text-gray-400 animate-fadeIn" style={{ animationDelay: '0.15s' }}>
                  <MapPin className="w-5 h-5" />
                  <span>{searchResult.searchedPerson.city}, {searchResult.searchedPerson.country}</span>
                      <span className="ml-auto text-xs opacity-70">
                        {searchResult.searchedPerson.created_at ? new Date(searchResult.searchedPerson.created_at).toLocaleDateString() : ''}
                  </span>
                    </div>
                    {/* Statut et certification */}
                    <div className="flex items-center justify-between gap-2 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                      <StatusBadge status={searchResult.searchedPerson.status} />
                      {searchResult.searchedPerson.confirmed && (
                        <div className="flex items-center gap-1 text-green-500">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm">{t('home.results.certified')}</span>
                    </div>
                      )}
                      {!searchResult.searchedPerson.confirmed && (
                        <div className="flex items-center gap-1 text-yellow-500">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">{t('home.results.pending')}</span>
                  </div>
                      )}
                </div>
                  </>
                )}
              </div>
            </div>

            {/* Composant de verrouillage */}
            {isResultLocked ? (
              <div 
                onClick={handleUnlock}
                className="relative cursor-pointer overflow-hidden transition-all duration-300 ease-in-out border-t border-gray-800 pt-3 mt-2"
              >
                <div 
                  id="lock-overlay"
                  className="absolute inset-0 bg-gradient-to-r from-primary-start/30 to-primary-end/30 backdrop-blur-md bg-black/70 flex items-center justify-center z-10 rounded-xl transition-all duration-300 ease-in-out"
                >
                  <div className="text-center p-4">
                    <Lock className="w-8 h-8 mx-auto mb-2 text-white" />
                    <h4 className="text-white font-bold text-lg mb-1">{t('home.results.unlockTitle')}</h4>
                    <p className="text-white text-sm opacity-80 mb-2">{t('home.results.unlockDescription')}</p>
                    {unlockPrice.amount > 0 && (
                      <p className="text-yellow-400 text-sm font-semibold mt-2">{formattedUnlockPrice}</p>
                    )}
                  </div>
                </div>
                
                <div className="opacity-5 pointer-events-none">
                  {/* Contenu flou pour le verrouillage */}
                  <div className="space-y-4">
                    {/* Localisation floue avec date */}
                    <div className="flex items-center gap-2 text-gray-400 mt-2">
                      <MapPin className="w-5 h-5" />
                      <span>{searchResult.searchedPerson.city}, {searchResult.searchedPerson.country}</span>
                      <span className="ml-auto text-xs opacity-70">
                        {searchResult.searchedPerson.created_at ? new Date(searchResult.searchedPerson.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                    
                    {/* Créateur de la relation - version floue */}
                    <div className="flex items-center gap-2 mb-2">
                      <User className={`w-5 h-5 ${gradientText}`} />
                      <span className={`font-medium ${gradientText}`}>
                        {t('home.results.createdBy')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 bg-black/20 p-3 rounded-xl">
                      <div className="w-16 md:w-14 h-16 md:h-14 rounded-full overflow-hidden flex-shrink-0">
                        <img 
                          src={searchResult.searchedPerson.creator.photo} 
                          alt={searchResult.searchedPerson.creator.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white text-base mb-0.5">
                          {searchResult.searchedPerson.creator.name}
                        </h4>
                        {searchResult.searchedPerson.creator.link && (
                          <button className={`${gradientButton} text-white text-xs px-3 py-1 rounded-lg flex items-center gap-1.5 hover:opacity-90 transition-opacity`}>
                            <LinkIcon className="w-3.5 h-3.5" />
                            {t('home.results.contact')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Créateur de la relation - version déverrouillée avec animation */}
                <div className="pt-3 border-t border-gray-800 animate-fadeIn transition-all duration-500 ease-in-out">
                  <div className="flex items-center gap-2 mb-2">
                <User className={`w-5 h-5 ${gradientText}`} />
                <span className={`font-medium ${gradientText}`}>
                  {t('home.results.createdBy')}
                </span>
              </div>
                  <div className="flex items-center gap-3 bg-black/20 p-3 rounded-xl">
                    <div className="w-16 md:w-14 h-16 md:h-14 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src={searchResult.searchedPerson.creator.photo} 
                    alt={searchResult.searchedPerson.creator.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white text-base mb-0.5">
                    {searchResult.searchedPerson.creator.name}
                  </h4>
                  {searchResult.searchedPerson.creator.link && (
                    <button
                      onClick={() => window.open(searchResult.searchedPerson.creator.link, '_blank', 'noopener,noreferrer')}
                          className={`${gradientButton} text-white text-xs px-3 py-1 rounded-lg flex items-center gap-1.5 hover:opacity-90 transition-opacity`}
                    >
                      <LinkIcon className="w-3.5 h-3.5" />
                      {t('home.results.contact')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Partenaire (si présent) */}
            {searchResult.searchedPerson.partner && (
                  <div className="pt-3 border-t border-gray-800 animate-fadeIn transition-all duration-500 ease-in-out" style={{ animationDelay: '0.2s' }}>
                    <div className="flex items-center gap-2 mb-2">
                  <Heart className={`w-5 h-5 ${gradientText}`} />
                  <span className={`font-medium ${gradientText}`}>
                        {t('profile.relations.status.en_couple')} avec
                  </span>
                </div>
                    <div className="flex items-center gap-3 bg-black/20 p-3 rounded-xl">
                      <div className="w-16 md:w-14 h-16 md:h-14 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src={searchResult.searchedPerson.partner.photo} 
                      alt={searchResult.searchedPerson.partner.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white text-base mb-0.5">
                      {searchResult.searchedPerson.partner.name}
                    </h4>
                    {searchResult.searchedPerson.partner?.link && (
                      <button
                        onClick={() => {
                          if (searchResult.searchedPerson.partner?.link) {
                            window.open(searchResult.searchedPerson.partner.link, '_blank', 'noopener,noreferrer');
                          }
                        }}
                            className={`${gradientButton} text-white text-xs px-3 py-1 rounded-lg flex items-center gap-1.5 hover:opacity-90 transition-opacity`}
                      >
                        <LinkIcon className="w-3.5 h-3.5" />
                        {t('home.results.contact')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
                )}

                {/* Autres relations trouvées */}
                {searchResult.searchedPerson.otherRelations && searchResult.searchedPerson.otherRelations.length > 0 && (
                  <div className="pt-3 border-t border-gray-800 animate-fadeIn transition-all duration-500 ease-in-out" style={{ animationDelay: '0.3s' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <User className={`w-5 h-5 ${gradientText}`} />
                      <span className={`font-medium ${gradientText}`}>
                        {t('home.results.otherRelations', { count: searchResult.searchedPerson.otherRelations.length })}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {searchResult.searchedPerson.otherRelations.map((relation, index) => (
                        <div key={index} className="bg-black/20 p-3 rounded-xl space-y-2">
                          <div className="flex items-center justify-between">
                            <StatusBadge status={relation.status} />
                            <span className="text-sm font-medium px-4 py-2 rounded-full bg-[#1A1A1A]" style={{ color: 'url(#matchGradient)' }}>
                              {relation.matchPercentage}% {t('home.results.match')}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Photo dans la liste des résultats */}
                            <div className="w-16 md:w-14 h-16 md:h-14 rounded-full overflow-hidden flex-shrink-0">
                              <img 
                                src={relation.creator.photo} 
                                alt={relation.creator.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-white text-sm">
                                  {relation.creator.name}
                                </h4>
                                {relation.confirmed && (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">
                                  {new Date(relation.created_at).toLocaleDateString()}
                                </span>
                                {relation.creator.link && (
                                  <button
                                    onClick={() => window.open(relation.creator.link, '_blank', 'noopener,noreferrer')}
                                    className={`${gradientButton} text-white text-xs px-2 py-0.5 rounded-lg flex items-center gap-1 hover:opacity-90 transition-opacity ml-2`}
                                  >
                                    <LinkIcon className="w-3 h-3" />
                                    {t('home.results.contact')}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          {relation.comment && (
                            <p className="text-gray-400 italic text-xs mt-2">
                              <span className="text-gray-500">Note : </span>
                              {relation.comment}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ajouter l'affichage du commentaire dans la partie déverrouillée */}
                {!isResultLocked && searchResult.searchedPerson.comment && (
                  <div className="pt-3 border-t border-gray-800 animate-fadeIn" style={{ animationDelay: '0.25s' }}>
                    <p className="text-gray-400 italic text-sm">
                      <span className="text-gray-500">Note : </span>
                      {isCommentExpanded || searchResult.searchedPerson.comment.length <= 50 
                        ? searchResult.searchedPerson.comment 
                        : `${searchResult.searchedPerson.comment.slice(0, 50)}...`}
                      {searchResult.searchedPerson.comment.length > 50 && (
                        <button
                          onClick={() => setIsCommentExpanded(!isCommentExpanded)}
                          className={`ml-2 text-xs ${gradientText} hover:opacity-80 transition-opacity`}
                        >
                          {isCommentExpanded ? t('common.seeLess') : t('common.seeMore')}
                        </button>
                      )}
                    </p>
              </div>
                )}
              </>
            )}
          </div>

          {waitingForDonation && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="text-center relative pt-6">
                <button
                  onClick={() => {
                    setWaitingForDonation(false);
                    setWaitingTime(90);
                    setLoadingDots('');
                    setShowUnlockConfirmation(false);
                  }}
                  className="absolute -top-4 -right-4 w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
                <button
                  onClick={() => {
                    setWaitingForDonation(false);
                    confirmUnlock();
                  }}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-primary-start to-primary-end text-white text-sm font-medium hover:opacity-90 transition-opacity animate-pulse-payment"
                >
                  {t('profile.deleteRelation.continueDonation')} ({formattedUnlockPrice})
                </button>
                <p className="text-yellow-500 font-medium text-sm mt-4 whitespace-pre-line opacity-80">
                  {t('profile.deleteRelation.waitingDonation')}{loadingDots}
              </p>
            </div>
          </div>
          )}

          {showThankYou && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="text-center animate-fadeIn">
                <div className="validation-icon">
                  <Check className="h-6 w-6" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Message "Aucun résultat" avec son appel à l'action */}
      {!searchResult && !isSearching && hasSearched && results && results.length === 0 && (
        <div ref={resultsRef} className="mt-6 p-4 bg-[#0A0A0A] rounded-2xl text-center">
          <p className="text-gray-400">{t('home.noResults')}</p>
        </div>
      )}

      {/* Modal de confirmation de déverrouillage */}
      {showUnlockConfirmation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] rounded-xl max-w-md w-full p-5 animate-fade-in-up">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-yellow-400" />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-2">
                {t('home.unlock.confirmTitle')}
              </h3>
              
              <p className="text-gray-400 mb-4">
                {t('home.unlock.confirmMessage')}
              </p>
              
              {unlockPrice.amount > 0 && (
              <div className="mt-3 p-2 bg-yellow-500/10 rounded-lg w-full">
                <p className="text-yellow-400 text-xs text-center">
                  <span className="font-semibold">{t('home.unlock.cost', { price: formattedUnlockPrice })}</span>
                </p>
              </div>
              )}
              
              <div className="flex gap-3 mt-6 w-full">
                <button
                  onClick={cancelUnlock}
                  className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                
                <button
                  onClick={confirmUnlock}
                  className={`flex-1 ${gradientButton} text-white py-3 px-4 rounded-lg flex items-center justify-center gap-2`}
                >
                  {t('home.unlock.action')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour afficher tous les résultats */}
      {showAllResults && results && (
        <div className="fixed inset-0 bg-black flex items-stretch z-50">
          <div className="w-full h-full bg-[#121212] flex flex-col">
            <div className="bg-[#121212] p-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className={`text-xl font-bold ${gradientText}`}>
                {t('home.results.allResults', { count: results.length })}
              </h3>
              <button
                onClick={() => setShowAllResults(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {results.map((result: ApiSearchResult, index: number) => (
                <div key={index} className="bg-black/20 p-4 rounded-xl">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Photo dans la liste des résultats */}
                    <div className="w-32 h-32 md:w-48 md:h-48 rounded-2xl overflow-hidden flex-shrink-0">
                      <img 
                        src={result.photo} 
                        alt={result.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0 space-y-3">
                      <h3 className="text-2xl font-semibold text-white">
                        {result.name}
                      </h3>
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <span className="text-sm font-medium px-4 py-2 rounded-full bg-[#1A1A1A]" style={{ color: 'url(#matchGradient)' }}>
                              {result.match_percentage}% {t('home.results.match')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowAllResults(false);
                          setSearchResult({
                            searchedPerson: {
                              photo: result.photo,
                              name: result.name,
                              country: result.country,
                              city: result.city,
                              status: result.status,
                              matchPercentage: result.match_percentage,
                              confirmed: result.confirmed,
                              created_at: result.created_at,
                              comment: result.comment,
                              searchCount: result.search_count || 0,
                              creator: {
                                name: result.creator_name,
                                photo: result.creator_photo,
                                link: result.creator_link || undefined
                              }
                            }
                          });
                        }}
                        className={`${gradientButton} text-white text-sm px-4 py-2 rounded-lg mt-4 hover:opacity-90 transition-opacity`}
                      >
                        {t('home.results.viewDetails')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Ajouter le modal de paiement */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <PaymentFlow 
            amount={unlockPrice.amount}
            currency={unlockPrice.currency}
            onSuccess={() => {
              confirmUnlock();
              setShowPaymentModal(false);
            }}
            onCancel={() => setShowPaymentModal(false)}
            service="SEARCH_DETAILS_UNLOCK"
            userName={user?.user_metadata?.full_name || user?.email || 'Utilisateur'}
          />
        </div>
      )}
    </div>
  );
}

export default Home;

